// --- Dependencies --------------------------------------------------------------------------------
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useAuth from "@/hooks/useAuth";
import useFetch from "@/hooks/useFetch";
import useStore from "@/hooks/useStore";
import useTranslation from "@/hooks/useTranslation";
import type { TyApiResponse, TyHomeAlert, TyHomeAlertLevel, TyHomeOverview } from "@/types";
import { buildMenuUrl, getMenuHost } from "@/utils";
import HomeScreenLoader from "./Loader";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type AlertConfig = { label: string; to: string; icon: string };

// --- Constants ----------------------------------------------------------------------------------

/** Danger first: a paused menu matters more than a category nobody filled in yet. */
const LEVEL_WEIGHT: Record<TyHomeAlertLevel, number> = { danger: 0, warning: 1, info: 2 };

const COPIED_RESET_MS = 2000;

// --- Helpers ------------------------------------------------------------------------------------

function greetingKey(): "greetingMorning" | "greetingAfternoon" | "greetingEvening" {
    const hour = new Date().getHours();
    if (hour < 12) return "greetingMorning";
    if (hour < 17) return "greetingAfternoon";
    return "greetingEvening";
}

/** The first word of a full name — "أهلا يا أحمد" reads better than the full three-part name. */
function firstName(name: string | undefined): string {
    return (name ?? "").trim().split(/\s+/)[0] ?? "";
}

// --- Sub-components -----------------------------------------------------------------------------

type StatTileProps = {
    icon: string;
    label: string;
    value: number;
    note?: string;
    onPress?: () => void;
};

function StatTile({ icon, label, value, note, onPress }: StatTileProps) {
    return (
        <button type="button" className="home-tile" onClick={onPress} disabled={!onPress}>
            <span className="home-tile-icon">
                <img src={icon} alt="" width={16} height={16} />
            </span>
            <span className="home-tile-value">{value}</span>
            <span className="home-tile-label">{label}</span>
            {note && <span className="home-tile-note">{note}</span>}
        </button>
    );
}

type AlertRowProps = {
    alert: TyHomeAlert;
    config: AlertConfig;
    daysLabel: string;
    onPress: () => void;
};

function AlertRow({ alert, config, daysLabel, onPress }: AlertRowProps) {
    return (
        <button type="button" className={`home-alert home-alert--${alert.level}`} onClick={onPress}>
            <span className="home-alert-icon">
                <img src={config.icon} alt="" width={15} height={15} />
            </span>
            <span className="home-alert-label">{config.label}</span>
            {alert.count !== undefined && <span className="home-alert-count">{alert.count}</span>}
            {alert.days !== undefined && (
                <span className="home-alert-count">
                    {alert.days} {daysLabel}
                </span>
            )}
            <img className="home-alert-chevron" src="/icons/arrow-dark-icon.svg" alt="" width={16} height={16} />
        </button>
    );
}

function HomeOverview() {
    const t = useTranslation();
    const navigate = useNavigate();
    const store = useStore((state) => state.store);
    const client = useAuth((state) => state.client);

    const copyTimerRef = useRef<number | null>(null);
    const [isCopied, setIsCopied] = useState(false);

    const { data, loading, error, invoke } = useFetch<TyApiResponse<TyHomeOverview>>("/home/");
    const overview = data?.data;

    const menuHost = store ? getMenuHost(store.slug) : "";
    const menuUrl = store ? buildMenuUrl(store.slug) : "";

    // --- Handlers --------------------------------------------------------------------------------

    const handleCopy = async () => {
        if (!menuUrl) return;
        await navigator.clipboard.writeText(menuUrl);
        setIsCopied(true);
        if (copyTimerRef.current) window.clearTimeout(copyTimerRef.current);
        copyTimerRef.current = window.setTimeout(() => setIsCopied(false), COPIED_RESET_MS);
    };

    // --- Alert mapping ---------------------------------------------------------------------------

    /** Copy and destination live here; the API only ever names the situation and its numbers. */
    const alertConfigs: Record<string, AlertConfig> = {
        menuEmpty: { label: t.home.alertMenuEmpty, to: "/menu", icon: "/icons/category-icon.svg" },
        productsUnpriced: { label: t.home.alertProductsUnpriced, to: "/menu", icon: "/icons/price-icon.svg" },
        categoriesMissing: { label: t.home.alertCategoriesMissing, to: "/menu", icon: "/icons/category-icon.svg" },
        categoriesEmpty: { label: t.home.alertCategoriesEmpty, to: "/menu", icon: "/icons/category-icon.svg" },
        productsInactive: { label: t.home.alertProductsInactive, to: "/menu", icon: "/icons/eye-closed-icon.svg" },
        branchSlotsUnused: { label: t.home.alertBranchSlotsUnused, to: "/subscriptions/branches", icon: "/icons/store-icon.svg" },
        branchesUnconfigured: { label: t.home.alertBranchesUnconfigured, to: "/branches", icon: "/icons/store-icon.svg" },
        subscriptionMissing: { label: t.home.alertSubscriptionMissing, to: "/subscriptions", icon: "/icons/timer-icon.svg" },
        subscriptionPending: { label: t.home.alertSubscriptionPending, to: "/subscriptions", icon: "/icons/timer-icon.svg" },
        subscriptionPaused: { label: t.home.alertSubscriptionPaused, to: "/subscriptions", icon: "/icons/timer-icon.svg" },
        subscriptionGrace: { label: t.home.alertSubscriptionGrace, to: "/subscriptions", icon: "/icons/timer-icon.svg" },
        subscriptionExpiring: { label: t.home.alertSubscriptionExpiring, to: "/subscriptions", icon: "/icons/timer-icon.svg" },
        supportContacted: { label: t.home.alertSupportContacted, to: "/support", icon: "/icons/notifications-icon.svg" },
    };

    /** An unknown key from a newer API is skipped rather than rendered as a blank row. */
    const alerts = [...(overview?.alerts ?? [])]
        .filter((alert) => alertConfigs[alert.key])
        .sort((a, b) => LEVEL_WEIGHT[a.level] - LEVEL_WEIGHT[b.level]);

    // --- Render ----------------------------------------------------------------------------------

    if (loading && !overview) return <HomeScreenLoader />;

    return (
        <div className="home-screen">
            <div className="home-screen-content">
                {/* --- Hero: greeting + live menu link ---------------------------------------- */}
                <section className="home-hero">
                    <p className="home-hero-greeting">
                        {t.home[greetingKey()]}
                        {client?.name ? `، ${firstName(client.name)}` : ""}
                    </p>
                    <p className="home-hero-note">{t.home.menuLive}</p>
                    <div className="home-hero-url">
                        <p className="home-hero-host" dir="ltr">
                            {menuHost}
                        </p>
                    </div>
                    <div className="home-hero-actions">
                        <button type="button" className="home-hero-btn home-hero-btn--primary" onClick={handleCopy}>
                            <img src={isCopied ? "/icons/check-icon.svg" : "/icons/duplicate-icon.svg"} alt="" width={15} height={15} />
                            {isCopied ? t.home.copied : t.home.copyLink}
                        </button>
                        {/* The preview works even without a subscription, unlike the public menu URL. */}
                        <a className="home-hero-btn" href="/preview-menu" target="_blank" rel="noopener noreferrer">
                            <img src="/icons/eye-opened-icon.svg" alt="" width={15} height={15} />
                            {t.home.previewMenu}
                        </a>
                    </div>
                </section>

                {error && (
                    <section className="home-error">
                        <p className="home-error-text">{error}</p>
                        <button type="button" className="home-error-retry" onClick={() => invoke()}>
                            {t.home.retry}
                        </button>
                    </section>
                )}

                {overview && (
                    <>
                        {/* --- Stats ---------------------------------------------------------- */}
                        <section className="home-section">
                            <p className="home-section-title">{t.home.statsTitle}</p>
                            <div className="home-tiles">
                                {overview.menu && (
                                    <>
                                        <StatTile
                                            icon="/icons/size-squared-icon.svg"
                                            label={t.home.statProducts}
                                            value={overview.menu.products}
                                            note={`${overview.menu.activeProducts} ${t.home.statActiveNote}`}
                                            onPress={() => navigate("/menu")}
                                        />
                                        <StatTile
                                            icon="/icons/category-icon.svg"
                                            label={t.home.statCategories}
                                            value={overview.menu.categories}
                                            onPress={() => navigate("/menu")}
                                        />
                                        <StatTile
                                            icon="/icons/discount-icon.svg"
                                            label={t.home.statDiscounts}
                                            value={overview.menu.discountedProducts}
                                            onPress={() => navigate("/menu")}
                                        />
                                    </>
                                )}
                                {overview.branches && (
                                    <StatTile
                                        icon="/icons/store-icon.svg"
                                        label={t.home.statBranches}
                                        value={overview.branches.total}
                                        note={overview.branches.slots !== null ? `${overview.branches.slots} ${t.home.statSlotsNote}` : undefined}
                                        onPress={() => navigate("/branches")}
                                    />
                                )}
                                {overview.clients && (
                                    <StatTile
                                        icon="/icons/client-icon.svg"
                                        label={t.home.statStaff}
                                        value={overview.clients.total}
                                        onPress={() => navigate("/clients")}
                                    />
                                )}
                                {overview.subscription?.status === "active" && (
                                    <StatTile
                                        icon="/icons/timer-icon.svg"
                                        label={t.home.statDaysLeft}
                                        value={overview.subscription.daysRemaining}
                                        onPress={() => navigate("/subscriptions")}
                                    />
                                )}
                            </div>
                        </section>

                        {/* --- Attention feed ------------------------------------------------- */}
                        <section className="home-section">
                            <p className="home-section-title">{t.home.attentionTitle}</p>
                            {alerts.length === 0 ? (
                                <div className="home-all-good">
                                    <span className="home-all-good-icon">
                                        <img src="/icons/check-icon.svg" alt="" width={20} height={20} />
                                    </span>
                                    <div className="home-all-good-text">
                                        <p className="home-all-good-title">{t.home.allGoodTitle}</p>
                                        <p className="home-all-good-subtitle">{t.home.allGoodSubtitle}</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="home-alerts">
                                    {alerts.map((alert) => (
                                        <AlertRow
                                            key={alert.key}
                                            alert={alert}
                                            config={alertConfigs[alert.key]}
                                            daysLabel={t.home.days}
                                            onPress={() => navigate(alertConfigs[alert.key].to)}
                                        />
                                    ))}
                                </div>
                            )}
                        </section>
                    </>
                )}
            </div>
        </div>
    );
}

// --- Logic --------------------------------------------------------------------------------------

export default function HomeScreen() {
    const storeId = useStore((state) => state.storeId);

    /**
     * Switching the active store only swaps the header and navigates to "/", so a client already on
     * Home would keep the previous store's overview. Keying on the store id remounts the whole
     * screen instead, which refetches and drops the stale numbers rather than showing them while the
     * new ones load.
     */
    return <HomeOverview key={storeId ?? "none"} />;
}
